import { type ReactNode, useEffect, useRef, useState } from "react";

// Generic popover shell (outside-click + Escape close). Sibling of the
// resources-filters / solutions-filters popovers; kept self-contained so the
// Pipeline Report filter feature owns its own chrome. Widened relative to those
// siblings' fixed w-80 — the Pipeline Report has ~15 filterable fields, which
// need real room (and a 2-column grid) rather than one long narrow stack.
export function FilterPopover({
  trigger,
  children,
}: {
  trigger: (open: boolean) => ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!(ref.current && e.target instanceof Node)) return;
      if (ref.current.contains(e.target)) return;
      // The pick-list filters (Resource / Resourcing Status / Resource Plan
      // Received / Owner / Platform) render via shadcn's Select, which portals
      // its dropdown to the end of <body> — outside this panel's own DOM
      // subtree. Without this check, clicking any option registers as an
      // "outside click" and closes the whole panel before the selection can
      // apply, which is exactly the bug this guards against (confirmed live:
      // picking a filter option appeared to do nothing). data-slot is the
      // shadcn convention stamped on every primitive's rendered root,
      // including Select's portaled content (see src/components/ui/select.tsx).
      if (e.target instanceof Element && e.target.closest('[data-slot="select-content"]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground transition hover:bg-muted/60"
      >
        {trigger(open)}
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-2 max-h-[80vh] w-[min(92vw,720px)] overflow-y-auto rounded-md border border-border bg-card p-4 shadow-lg">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
